// frontend/src/features/livestock/LivestockCard.tsx

import { useNavigate } from 'react-router-dom';
import { Card, Group, Text, Badge, Stack, Avatar } from '@mantine/core';
import { formatAge } from '@/utils/formatAge';
import type { Livestock } from '@/types/livestock';

interface LivestockCardProps {
  livestock: Livestock;
  tankId: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'alive': return 'green';
    case 'deceased': return 'red';
    case 'sold': return 'blue';
    default: return 'gray';
  }
};

export function LivestockCard({ livestock, tankId }: LivestockCardProps) {
  const navigate = useNavigate();
  const isCoral = livestock.livestock_type === 'coral';
  const coralSubtitle = [livestock.category, livestock.subcategory].filter(Boolean).join(' · ');

  return (
    <Card padding="md" withBorder onClick={() => navigate(`/tanks/${tankId}/livestock/${livestock.id}`)} style={{ cursor: 'pointer' }}>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          {livestock.thumbnail_url && (
            <Avatar src={livestock.thumbnail_url} size={32} radius="sm" />
          )}
          <div>
            <Text fw={500}>{livestock.common_name}</Text>
            {isCoral && coralSubtitle && (
              <Text size="xs" c="dimmed">{coralSubtitle}</Text>
            )}
            {!isCoral && livestock.scientific_name && (
              <Text size="xs" c="dimmed" fs="italic">{livestock.scientific_name}</Text>
            )}
          </div>
        </Group>
        {livestock.status !== 'unknown' && (
          <Badge color={getStatusColor(livestock.status)} size="sm">
            {livestock.status}
          </Badge>
        )}
      </Group>
      <Group gap="lg">
        <Stack gap={0}>
          <Text size="xs" c="dimmed">Age</Text>
          <Text size="sm">{formatAge(livestock.acquisition_date, livestock.deceased_date)}</Text>
        </Stack>
        <Stack gap={0}>
          <Text size="xs" c="dimmed">Qty</Text>
          <Text size="sm">{livestock.quantity}</Text>
        </Stack>
        {livestock.vendor && (
          <Stack gap={0}>
            <Text size="xs" c="dimmed">From</Text>
            <Text size="sm">{livestock.vendor}</Text>
          </Stack>
        )}
      </Group>
    </Card>
  );
}
